import { jsPDF } from 'jspdf';
import { getPdfBackgroundDataUrl } from './pdfBackground';

export interface PublicReceiptData {
  protocolNumber: string;
  reporterName?: string;
  address: string;
  neighborhood: string;
  poleBusbar: string;
  status: string;
  statusLabel: string;
  openedAt: string; // ISO
}

const STATUS_LABELS: Record<string, string> = {
  ABERTA: 'Aberta',
  AVULSA: 'Avulsa',
  EM_ANALISE: 'Em Análise',
  EM_ANDAMENTO: 'Em Andamento',
  PENDENTE: 'Pendente',
  CONCLUIDA: 'Concluída',
  EM_ATRASO: 'Em Atraso',
};

const formatDate = (date: Date) => date.toLocaleDateString('pt-BR');
const formatTime = (date: Date) => date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  return `${formatDate(d)} as ${formatTime(d)}`;
};

export async function downloadPublicReceipt(data: PublicReceiptData): Promise<void> {
  const document = new jsPDF({ unit: 'mm', format: 'a4' });
  const contentTopOffset = 25;
  const pageWidth = document.internal.pageSize.getWidth();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  const pageHeight = document.internal.pageSize.getHeight();
  const backgroundDataUrl = await getPdfBackgroundDataUrl();

  if (backgroundDataUrl) {
    document.addImage(backgroundDataUrl, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
  }

  const drawCard = (
    x: number,
    y: number,
    width: number,
    height: number,
    label: string,
    value: string,
    maxLines = 2,
  ) => {
    document.setDrawColor(222, 226, 230);
    document.setFillColor(248, 249, 250);
    document.roundedRect(x, y, width, height, 2, 2, 'FD');

    document.setTextColor(73, 80, 87);
    document.setFont('helvetica', 'bold');
    document.setFontSize(7.5);
    document.text(label, x + 2.5, y + 3.5);

    document.setTextColor(33, 37, 41);
    document.setFont('helvetica', 'normal');
    document.setFontSize(8);

    const textWidth = width - 6;
    const allLines = document.splitTextToSize(value || '-', textWidth);
    const lines = allLines.slice(0, maxLines);
    if (allLines.length > maxLines && lines.length > 0) {
      lines[lines.length - 1] = `${lines[lines.length - 1]}...`;
    }
    document.text(lines, x + 2.5, y + 8);
  };

  document.setFillColor(52, 152, 219);
  document.roundedRect(margin, 10 + contentTopOffset, contentWidth, 12, 3, 3, 'F');
  document.setTextColor(255, 255, 255);
  document.setFont('helvetica', 'bold');
  document.setFontSize(11);
  document.text('Comprovante de Solicitacao', margin + 5, 16 + contentTopOffset);
  document.setFontSize(7.5);
  document.text('Iluminacao Publica', margin + 5, 19 + contentTopOffset);

  document.setTextColor(33, 37, 41);

  const colGap = 4;
  const colWidth = (contentWidth - colGap) / 2;

  drawCard(
    margin,
    26 + contentTopOffset,
    colWidth,
    11,
    'PROTOCOLO',
    data.protocolNumber?.trim() || '-',
    1,
  );
  drawCard(
    margin + colWidth + colGap,
    26 + contentTopOffset,
    colWidth,
    11,
    'STATUS ATUAL',
    data.statusLabel?.trim() || STATUS_LABELS[data.status] || data.status || '-',
    1,
  );

  drawCard(margin, 40 + contentTopOffset, contentWidth, 11, 'SOLICITANTE', data.reporterName?.trim() || '-', 1);

  drawCard(margin, 54 + contentTopOffset, contentWidth, 13, 'ENDERECO', data.address?.trim() || '-', 2);

  drawCard(margin, 70 + contentTopOffset, colWidth, 11, 'BAIRRO', data.neighborhood?.trim() || '-', 1);
  drawCard(
    margin + colWidth + colGap,
    70 + contentTopOffset,
    colWidth,
    11,
    'BARRAMENTO',
    data.poleBusbar?.trim() || '-',
    1,
  );

  drawCard(
    margin,
    84 + contentTopOffset,
    contentWidth,
    11,
    'DATA E HORA DE ABERTURA',
    data.openedAt ? formatDateTime(data.openedAt) : '-',
    1,
  );

  document.setDrawColor(206, 212, 218);
  document.line(margin, 100 + contentTopOffset, pageWidth - margin, 100 + contentTopOffset);
  document.setFont('helvetica', 'normal');
  document.setFontSize(7.5);
  document.text(
    `Emitido em ${formatDate(new Date())} as ${formatTime(new Date())}. Guarde este comprovante para acompanhamento.`,
    margin,
    105 + contentTopOffset,
  );
  document.text(
    'Consulte o andamento em: https://iluminacaopublica.pesqueira.pe.gov.br/consultar',
    margin,
    110 + contentTopOffset,
  );

  const filename = `comprovante-${data.protocolNumber.replace(/[^A-Z0-9]/g, '_')}.pdf`;
  document.save(filename);
}

export function statusToLabel(status: string): string {
  return STATUS_LABELS[status] || status;
}
